import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMeetingDTO,
  ListMeetingActionsQuery,
  ListMeetingsQuery,
  UpdateMeetingDTO,
} from "@/modules/management/meeting-minutes/domain/types";
import { meetingMinutesApi } from "./client";

const queryKey = ["management", "meetings"] as const;

export function useMeetingsQuery(params: ListMeetingsQuery) {
  return useQuery({
    queryKey: [...queryKey, "list", params],
    queryFn: () => meetingMinutesApi.list(params),
  });
}

export function useMeetingActionsQuery(params: ListMeetingActionsQuery) {
  return useQuery({
    queryKey: [...queryKey, "actions", params],
    queryFn: () => meetingMinutesApi.listActions(params),
  });
}

export function useMeetingByIdQuery(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...queryKey, "detail", id],
    queryFn: () => meetingMinutesApi.getById(id!),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateMeetingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateMeetingDTO) => meetingMinutesApi.create(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey[0], queryKey[1]] });
    },
  });
}

export function useUpdateMeetingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateMeetingDTO }) =>
      meetingMinutesApi.update(id, dto),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [queryKey[0], queryKey[1]] });
      qc.invalidateQueries({ queryKey: [...queryKey, "detail", id] });
    },
  });
}

export function useDeleteMeetingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingMinutesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey[0], queryKey[1]] });
    },
  });
}

export function useUpdateMeetingActionStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      actionId,
      status,
    }: {
      meetingId: string;
      actionId: string;
      status: "pending" | "done";
    }) => meetingMinutesApi.updateActionStatus(meetingId, actionId, status),
    onSuccess: (_, { meetingId }) => {
      qc.invalidateQueries({ queryKey: [queryKey[0], queryKey[1]] });
      qc.invalidateQueries({ queryKey: [...queryKey, "detail", meetingId] });
    },
  });
}
